import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { OrdersWithPayment } from 'src/app/models/order';
import { OrderService } from 'src/app/order.service';
import { EarnProgressItem, ImmediateSuggestion } from 'src/app/models/earned-reward';
import { EarnProgressDetailDialogComponent } from './earn-progress-detail-dialog.component';

@Component({
  selector: 'app-earn-progress',
  templateUrl: './earn-progress.component.html',
  styleUrls: ['./earn-progress.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EarnProgressComponent implements OnInit, OnChanges {
  @Input() orderWithPayment: OrdersWithPayment;
  @Output() refresh = new EventEmitter<boolean>();
  @Output() qualifyingOffers = new EventEmitter<number>();
  @Output() suggestions = new EventEmitter<ImmediateSuggestion[]>();

  progressItems: EarnProgressItem[] = [];
  immediateSuggestions: ImmediateSuggestion[] = [];
  selectedOfferId: number | null = null;
  isSelecting = false;

  constructor(
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadProgress();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderWithPayment && !changes.orderWithPayment.firstChange) {
      this.loadProgress();
    }
  }

  get promoApplied(): boolean {
    return !!(this.orderWithPayment && this.orderWithPayment.bot_order
      && this.orderWithPayment.bot_order.promo_code);
  }

  get hasContent(): boolean {
    return this.progressItems.length > 0;
  }

  private loadProgress(): void {
    const orderHash = this.orderWithPayment && this.orderWithPayment.bot_order
      && this.orderWithPayment.bot_order.order_hash;
    if (!orderHash) return;

    this.orderService.getEarnProgress(orderHash).subscribe({
      next: (data) => {
        this.progressItems = (data && data.progress) || [];
        this.selectedOfferId = data && data.selected_offer_id;
        this.immediateSuggestions = (data && data.immediate_suggestions) || [];
        const count = this.promoApplied ? 0 : this.immediateSuggestions.length;
        this.qualifyingOffers.emit(count);
        this.suggestions.emit(this.promoApplied ? [] : this.immediateSuggestions);
        this.cdr.markForCheck();
      },
      error: () => {}
    });
  }

  getMessage(item: EarnProgressItem): string {
    if (item.qualified) {
      return `Earn ${item.reward_description} for your next visit!`;
    }
    if (item.trigger_type === 'SPEND') {
      return `Add \$${item.spend_remaining.toFixed(2)} more to earn ${item.reward_description}`;
    }
    const missing = (item.conditions || []).filter(c => !c.met);
    if (missing.length > 0) {
      const names = missing.map(c => c.name).join(', ');
      return `Add ${names} to earn ${item.reward_description}`;
    }
    return `Complete your order to earn ${item.reward_description}!`;
  }

  getProgress(item: EarnProgressItem): number {
    if (item.trigger_type !== 'SPEND' || !item.spend_threshold) return 0;
    return Math.min(100, (item.spend_current / item.spend_threshold) * 100);
  }

  selectOffer(item: EarnProgressItem): void {
    if (this.isSelecting) return;
    const orderHash = this.orderWithPayment.bot_order.order_hash;
    const newId = this.selectedOfferId === item.offer_id ? null : item.offer_id;
    this.isSelecting = true;
    this.orderService.selectEarnOffer(orderHash, newId).subscribe({
      next: (res) => {
        this.selectedOfferId = res.selected_offer_id;
        this.isSelecting = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSelecting = false;
        this.cdr.markForCheck();
      }
    });
  }

  openDetails(item: EarnProgressItem): void {
    this.dialog.open(EarnProgressDetailDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      data: item,
      panelClass: 'epd-dialog-panel'
    });
  }
}
